import {ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideZonelessChangeDetection} from '@angular/core';
import {provideServiceWorker} from '@angular/service-worker';
import {provideRouter} from '@angular/router';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi} from '@angular/common/http';
import {provideAnimations} from '@angular/platform-browser/animations';
import {authInterceptor, provideAuth} from 'angular-auth-oidc-client';
import {DatePipe} from '@angular/common';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {DayJsDateAdapter, DAYJS_DATE_FORMATS} from '@xmedical/ui';
import {FILE_STORAGE_PROVIDER, CoreFileStorageProvider} from '@xmedical/uploads';
import {firstValueFrom, of} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {
  ApplicationInterceptor,
  HttpErrorInterceptor,
  PermissionProvider,
  PUSHER_CONFIG,
  RequestTracingInterceptor,
  WebSocketInterceptor,
  XMedicalCoreModule
} from '@xmedical/core';
import * as Sentry from '@sentry/angular';

import {routes} from './app.routes';
import {authConfig} from './core/auth/auth.config';
import {environment} from '../environments/environment';
import {PortalPermissionProvider} from './core/auth/portal-permission.provider';
import {AuthenticationService} from './core/auth/authentication.service';
import {OrganisationStore} from './core/stores/organisation.store';
import {LocalStoragePatientDraftStore} from './core/services/local-storage-patient-draft-store.service';
import {XCS_PATIENT_DRAFT_STORE} from '@xmedical/clinical-shared';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([authInterceptor()])
    ),
    {provide: DateAdapter, useClass: DayJsDateAdapter, deps: [MAT_DATE_LOCALE]},
    {provide: MAT_DATE_FORMATS, useValue: DAYJS_DATE_FORMATS},
    DatePipe,

    // XMedical Core library — base URL without /v1 for org/ prefix support
    importProvidersFrom(
      XMedicalCoreModule.forRoot({
        apiUrl: environment.api + '/v1',
        adminApiUrl: environment.api + '/admin/v1',
        version: environment.version,
        storageKeys: {
          accessToken: 'access_token',
          selectedPractice: 'selected_practice',
          installationId: 'installation_id',
          sessionId: 'session_state',
          socketId: 'socket_id'
        }
      })
    ),

    provideAuth(authConfig),

    // Boot order: run OIDC checkAuth, then — if authenticated — initialise
    // OrganisationStore (load orgs, hydrate selectedOrganisation from
    // localStorage, load locations + role for that org). Route activation
    // waits on the returned promise, so guards see a consistent
    // selectedOrganisationId / initialised() on first navigation. Errors are
    // swallowed to "fail open": a busted org load shouldn't prevent the
    // login redirect or block the app from booting.
    provideAppInitializer(() => {
      const auth = inject(AuthenticationService);
      const orgStore = inject(OrganisationStore);
      return firstValueFrom(
        auth.bootstrap().pipe(
          switchMap(({ isAuthenticated }) =>
            isAuthenticated ? orgStore.initialise() : of(void 0)
          ),
          catchError((err) => {
            console.error('App initialiser failed', err);
            return of(void 0);
          })
        )
      );
    }),

    // No PracticeInterceptor — portal operates above practice level
    {provide: HTTP_INTERCEPTORS, useClass: ApplicationInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: WebSocketInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: RequestTracingInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true},

    // Error handler
    {provide: ErrorHandler, useValue: Sentry.createErrorHandler({showDialog: false})},

    {provide: PermissionProvider, useClass: PortalPermissionProvider},

    {provide: PUSHER_CONFIG, useValue: environment.pusher},
    CoreFileStorageProvider,
    {provide: FILE_STORAGE_PROVIDER, useExisting: CoreFileStorageProvider},

    // Bind the lib's XCS_PATIENT_DRAFT_STORE token to the portal's
    // localStorage-backed implementation. Key namespaced by
    // ${orgId}-${practiceId}-${userId} so drafts cannot leak across orgs,
    // practices, or users on a shared device.
    {provide: XCS_PATIENT_DRAFT_STORE, useExisting: LocalStoragePatientDraftStore},

    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorkerEnabled,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ]
};
